import { useSlotId } from '@react-aria/utils';
import { clsx } from 'clsx';
import { type HTMLMotionProps, motion, useReducedMotion } from 'motion/react';
import {
    type AriaAttributes,
    Children,
    cloneElement,
    createContext,
    type ForwardedRef,
    forwardRef,
    isValidElement,
    memo,
    type ReactNode,
    type RefCallback,
    useContext,
    useId,
    useLayoutEffect,
    useState,
} from 'react';
import type { PressEvent, PressHookProps } from 'react-aria';
import {
    type ContextValue,
    DisclosureGroupStateContext,
    DisclosureStateContext,
    Provider,
    useContextProps,
} from 'react-aria-components';
import {
    type DisclosureState,
    type DisclosureProps as StatelyDisclosureProps,
    useDisclosureState,
} from 'react-stately';

import { Icon, type SVGIconProps } from '@gitbook/spine-icons';
import { useMergedRefs } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

export interface DisclosureProps extends StatelyDisclosureProps {
    children: ReactNode | ((renderProps: DisclosureState) => ReactNode);
    id?: string;
}

export {
    DisclosureGroup,
    type DisclosureGroupProps,
    DisclosureStateContext,
} from 'react-aria-components';

export function Disclosure(props: DisclosureProps) {
    const groupState = useContext(DisclosureGroupStateContext);
    const defaultId = useId();
    const id = props.id || defaultId;
    const isExpanded = groupState ? groupState.expandedKeys.has(id) : props.isExpanded;
    const state = useDisclosureState({
        ...props,
        isExpanded,
        onExpandedChange: (isExpanded) => {
            groupState?.toggleKey(id);
            props.onExpandedChange?.(isExpanded);
        },
    });
    const panelId = useSlotId();
    return (
        <DisclosureStateContext.Provider value={state}>
            <Provider
                values={[
                    [DisclosureStateContext, state],
                    [DisclosureTriggerContext, { 'aria-controls': panelId }],
                    [DisclosurePanelContext, { id: panelId }],
                ]}
            >
                {typeof props.children === 'function' ? props.children(state) : props.children}
            </Provider>
        </DisclosureStateContext.Provider>
    );
}

export interface DisclosureTriggerProps {
    /**
     * The children to render. Must be a single React element.
     */
    children?: ReactNode;
    /**
     * The value of the `aria-haspopup` attribute.
     */
    'aria-haspopup'?: AriaAttributes['aria-haspopup'];

    /**
     * The value of the `aria-controls` attribute.
     */
    'aria-controls'?: AriaAttributes['aria-controls'];
}

const DisclosureTriggerContext = createContext<ContextValue<DisclosureTriggerProps, HTMLElement>>(
    {}
);

export const DisclosureTrigger = forwardRef(function DisclosureTrigger(
    props: DisclosureTriggerProps,
    ref: ForwardedRef<HTMLElement>
) {
    [props, ref] = useContextProps(props, ref, DisclosureTriggerContext);
    const state = useContext(DisclosureStateContext);
    assert(state, 'DisclosureTrigger must be used within a Disclosure');

    const child = Children.only(props.children);
    assert(
        isValidElement<
            { onPress: PressHookProps['onPress'] } & AriaAttributes & {
                    id: string;
                    ref: RefCallback<HTMLElement | null>;
                }
        >(child),
        'DisclosureTrigger must have a single React element as its child.'
    );

    const mergedRef = useMergedRefs<HTMLElement>(ref, (child as any).ref);

    return cloneElement(child, {
        ref: mergedRef,
        onPress: (event: PressEvent) => {
            child.props.onPress?.(event);
            state.toggle();
        },
        'aria-haspopup': props['aria-haspopup'] ?? 'true',
        'aria-expanded': state.isExpanded,
        'aria-controls': props['aria-controls'],
    });
});

type DisclosureAnimateProp = 'size' | 'opacity';

export interface DisclosurePanelProps extends HTMLMotionProps<'div'> {
    /**
     * The orientation of the panel.
     */
    orientation?: 'vertical' | 'horizontal';

    /**
     * The properties to animate.
     */
    animateProps?: DisclosureAnimateProp[];
}

const DisclosurePanelContext = createContext<ContextValue<DisclosurePanelProps, HTMLDivElement>>(
    {}
);

export const DisclosurePanel = forwardRef(function DisclosurePanel(
    props: DisclosurePanelProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    [props, ref] = useContextProps(props, ref, DisclosurePanelContext);
    const { animateProps = ['size', 'opacity'], orientation = 'vertical' } = props;
    const state = useContext(DisclosureStateContext);
    assert(state, 'DisclosurePanel must be used within a Disclosure');
    const prefersReducedMotion = useReducedMotion();
    const [animationState, setAnimationState] = useState<
        'entering' | 'entered' | 'exiting' | 'exited'
    >(state.isExpanded ? 'exited' : 'entered');
    useLayoutEffect(() => {
        setAnimationState(state.isExpanded ? 'entering' : 'exiting');
    }, [state.isExpanded]);
    return (
        <motion.div
            ref={ref}
            {...props}
            className={clsx('overflow-clip', props.className)}
            initial={false}
            transition={{ duration: prefersReducedMotion ? 0 : 0.2, ease: 'easeInOut' }}
            variants={
                orientation === 'vertical'
                    ? {
                          expanded: {
                              height: animateProps.includes('size') ? 'auto' : undefined,
                              opacity: animateProps.includes('opacity') ? 1 : undefined,
                          },
                          collapsed: {
                              height: animateProps.includes('size') ? 0 : undefined,
                              opacity: animateProps.includes('opacity') ? 0 : undefined,
                          },
                      }
                    : {
                          expanded: {
                              width: animateProps.includes('size') ? 'auto' : undefined,
                              opacity: animateProps.includes('opacity') ? 1 : undefined,
                          },
                          collapsed: {
                              width: animateProps.includes('size') ? 0 : undefined,
                              opacity: animateProps.includes('opacity') ? 0 : undefined,
                          },
                      }
            }
            animate={state.isExpanded ? 'expanded' : 'collapsed'}
            aria-hidden={!state.isExpanded}
            style={{
                contentVisibility: animationState === 'exited' ? 'hidden' : 'visible',
            }}
            onAnimationComplete={(animation) => {
                if (animation === 'collapsed' && animationState === 'exiting') {
                    setAnimationState('exited');
                } else if (animation === 'expanded' && animationState === 'entering') {
                    setAnimationState('entered');
                }
            }}
        >
            {props.children}
        </motion.div>
    );
});

export interface DisclosureChevronProps extends SVGIconProps {}

export const DisclosureChevron = memo(function DisclosureChevron(props: DisclosureChevronProps) {
    const state = useContext(DisclosureStateContext);
    assert(state, 'DisclosureChevron must be used within a Disclosure');
    return (
        <Icon.ChevronRight
            {...props}
            className={clsx(
                'shrink-0 transition',
                state.isExpanded && 'rotate-90',
                props.className
            )}
        />
    );
});
